import { useState, useCallback, useRef } from "react";
import { CircularProgress, IconButton, Tooltip } from "@material-ui/core";
import ReactToPrint from "react-to-print";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import { Print } from "@mui/icons-material";

const MAX_RECORDS_LENGTH = 1000;
const RENDER_DELAY = 500;

export default function PrintButton({ selectRef, componentRef, dataTable }) {
  const [isPrinting, setIsPrinting] = useState(false);
  const previousPageSize = useRef(null);
  const snackbar = useSnackbar();

  const handleBeforePrint = useCallback(async () => {
    if (!dataTable || dataTable.length === 0) {
      snackbar.showMessage("Não há dados para imprimir.", {
        severity: "error",
      });
      return;
    }

    if (dataTable.length > MAX_RECORDS_LENGTH) {
      snackbar.showMessage(
        `A impressão está limitada a ${MAX_RECORDS_LENGTH} registros. Você tem ${dataTable.length} registros. Refine os filtros e tente novamente.`,
        {
          severity: "error",
        }
      );
      return;
    }

    setIsPrinting(true);

    if (selectRef?.current) {
      previousPageSize.current = selectRef.current.value;

      const event = new Event("change", { bubbles: true });
      selectRef.current.value = dataTable.length.toString();
      selectRef.current.dispatchEvent(event);

      await new Promise((resolve) => setTimeout(resolve, RENDER_DELAY));
    }
  }, [dataTable, selectRef, snackbar]);

  const handleAfterPrint = useCallback(() => {
    if (selectRef?.current && previousPageSize.current) {
      const event = new Event("change", { bubbles: true });
      selectRef.current.value = previousPageSize.current;
      selectRef.current.dispatchEvent(event);
      previousPageSize.current = null;
    }

    setIsPrinting(false);
  }, [selectRef]);

  const handlePrintError = useCallback(
    (error) => {
      setIsPrinting(false);

      if (selectRef?.current && previousPageSize.current) {
        const event = new Event("change", { bubbles: true });
        selectRef.current.value = previousPageSize.current;
        selectRef.current.dispatchEvent(event);
        previousPageSize.current = null;
      }

      snackbar.showMessage("Erro ao imprimir. Tente novamente.", {
        severity: "error",
      });
    },
    [selectRef, snackbar]
  );

  const getContent = useCallback(() => {
    return componentRef?.current;
  }, [componentRef]);

  const canPrint =
    dataTable && dataTable.length > 0 && dataTable.length <= MAX_RECORDS_LENGTH;

  return (
    <ReactToPrint
      trigger={() => (
        <Tooltip
          title={
            !canPrint && dataTable?.length > MAX_RECORDS_LENGTH
              ? `Máximo de ${MAX_RECORDS_LENGTH} registros para impressão`
              : "Imprimir página"
          }
          placement="bottom"
        >
          <span>
            <IconButton disabled={isPrinting || !canPrint} size="small">
              {isPrinting ? <CircularProgress size={24} /> : <Print />}
            </IconButton>
          </span>
        </Tooltip>
      )}
      content={getContent}
      onBeforeGetContent={handleBeforePrint}
      onAfterPrint={handleAfterPrint}
      onPrintError={handlePrintError}
      documentTitle={`Impressão - ${new Date().toLocaleDateString("pt-BR")}`}
      pageStyle={`
        @page {
          size: A4;
          margin: 20mm;
        }
        @media print {
          body {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          table {
            page-break-inside: auto;
          }
          tr {
            page-break-inside: avoid;
            page-break-after: auto;
          }
          thead {
            display: table-header-group;
          }
          tfoot {
            display: table-footer-group;
          }
        }
      `}
    />
  );
}
